/**
 * A described tool in each provider's wire shape. The viewer's tools carry no
 * prose (`toolDef.ts`), so a host describes a tool first, from its own text,
 * and then projects it. Every function here is pure: the same tool gives the
 * same bytes, which keeps a provider's prompt cache. A projection holds the
 * schema it was given (Gemini's a cut copy), not a copy of it.
 */

import type { JsonSchema, ToolInputSchema } from './toolDef';

/** A tool with the text a model reads: what it is for, and its schema with each field's `description` set. */
export interface DescribedTool {
    readonly name: string;
    readonly description: string;
    readonly inputSchema: JsonSchema | ToolInputSchema;
}

const schemaOf = (tool: DescribedTool): JsonSchema => tool.inputSchema as JsonSchema;

export interface ResponsesTool {
    type: 'function';
    name: string;
    description: string;
    parameters: JsonSchema;
    /**
     * Always false: the tools' schemas have optional fields, and Responses
     * reads an omitted `strict` as true, which makes every field required.
     */
    strict: false;
}

/** The OpenAI Responses function tool. */
export function toResponsesTool(tool: DescribedTool): ResponsesTool {
    return { type: 'function', name: tool.name, description: tool.description, parameters: schemaOf(tool), strict: false };
}

export interface AnthropicTool {
    name: string;
    description: string;
    input_schema: JsonSchema;
}

/** The Anthropic Messages tool. */
export function toAnthropicTool(tool: DescribedTool): AnthropicTool {
    return { name: tool.name, description: tool.description, input_schema: schemaOf(tool) };
}

export interface GeminiTool {
    name: string;
    description: string;
    parametersJsonSchema: JsonSchema;
}

/** The JSON Schema keywords Gemini's `parametersJsonSchema` reads; every other keyword is dropped. */
const GEMINI_KEYWORDS = new Set([
    '$id', '$defs', '$ref', '$anchor', 'type', 'format', 'title', 'description', 'enum', 'items', 'prefixItems',
    'minItems', 'maxItems', 'minimum', 'maximum', 'anyOf', 'oneOf', 'properties', 'additionalProperties', 'required',
]);
/** Keywords whose value is a schema, a list of schemas, or a map of names to schemas. */
const ONE_SCHEMA = new Set(['items', 'additionalProperties']);
const SCHEMA_LIST = new Set(['prefixItems', 'anyOf', 'oneOf']);
const SCHEMA_MAP = new Set(['properties', '$defs']);

/** The Gemini function declaration, its schema cut to the keywords Gemini reads. */
export function toGeminiTool(tool: DescribedTool): GeminiTool {
    return { name: tool.name, description: tool.description, parametersJsonSchema: geminiSchema(schemaOf(tool)) };
}

function geminiSchema(node: JsonSchema): JsonSchema {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(node)) {
        if (!GEMINI_KEYWORDS.has(key)) continue;
        if (ONE_SCHEMA.has(key) && value && typeof value === 'object' && !Array.isArray(value)) out[key] = geminiSchema(value as JsonSchema);
        else if (SCHEMA_LIST.has(key) && Array.isArray(value)) out[key] = (value as JsonSchema[]).map(geminiSchema);
        else if (SCHEMA_MAP.has(key) && value && typeof value === 'object') {
            out[key] = Object.fromEntries(Object.entries(value as Record<string, JsonSchema>).map(([name, schema]) => [name, geminiSchema(schema)]));
        } else out[key] = value;
    }
    return out;
}
